import { Box, Button, Divider, Flex, Icon, Text } from '@chakra-ui/react'
import { TbCheck } from 'react-icons/tb'
import type { LessonUpcomingRow } from '../../api/private-classes/LessonSlots'
import ClickableAddress from '../address/ClickableAddress'
import { LazyLoadedImage } from '../lazy/LazyLoadedImage'
import LessonBookingBadge from './LessonBookingBadge'

const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 1 && minutes === 0) return '1 hour'
  if (hours === 2 && minutes === 0) return '2 hours'
  if (hours === 3 && minutes === 0) return '3 hours'
  return `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim()
}

const LessonBookingRow = ({
  lesson,
  onCancel,
  isTablet = false,
  isPast = false,
}: {
  lesson: LessonUpcomingRow
  onCancel?: () => void
  isTablet?: boolean
  isPast?: boolean
}) => {
  const location = lesson.location
  const tz = location?.timezone || lesson.venue.timezone || 'UTC'
  const locationLabel = location?.label || lesson.venue.name
  const address = location?.address || lesson.venue.address
  const thumbnail = location?.image || (lesson.venue.images && lesson.venue.images.length > 0 ? lesson.venue.images[0] : '')
  const courtLabel = lesson.court.court_label || locationLabel
  const timeLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  }).format(new Date(lesson.start_time))

  const isCancelled = (lesson as { status?: string }).status === 'cancelled'

  return (
    <Box
      bg="white"
      borderRadius="10px"
      boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
      w="100%"
      borderWidth="1px"
      borderColor="#DCE2EA"
      overflow="hidden"
      pos="relative"
      mb="12px"
    >
      <Box p={isTablet ? '15px 15px 0 15px' : '20px 20px 0 20px'}>
        <Flex gap="0px">
          <LazyLoadedImage
            w="calc(50% - 20px)"
            height={(!isTablet ? 152 : 120) + 20}
            src={thumbnail}
            borderRadius="10px"
            mr="20px"
          />
          <Box w="50%">
            {!isCancelled && (
              <Flex
                gap="2px"
                padding="2px 10px"
                borderRadius="50px"
                bg={isPast ? '#171C22' : '#22C55E'}
                w="fit-content"
              >
                <Text fontSize="sm" color="white">
                  {isPast ? 'Completed' : 'Going'}
                </Text>
                {!isPast && <Icon as={TbCheck} color="white" />}
              </Flex>
            )}
            <Text variant="title" fontSize={isTablet ? 'lg' : undefined} mt="2px">
              {`Lesson with ${lesson.coach.display_name}`}
            </Text>
            <Box mt={isTablet ? '6px' : '8px'}>
              <LessonBookingBadge />
            </Box>
          </Box>
        </Flex>
      </Box>

      <Box p={isTablet ? '15px' : '20px'}>
        <Flex mb={isTablet ? '5px' : '12px'}>
          <Flex alignItems="center" gap="4px" w="50%">
            <Text variant="header" fontSize={isTablet ? '12' : '15'}>
              Time:
            </Text>
            <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
              {timeLabel}
            </Text>
          </Flex>

          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize={isTablet ? '12' : '15'}>
              Type:
            </Text>
            <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
              Lesson
            </Text>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="4px" mb={isTablet ? '5px' : '12px'}>
          <Text variant="header" fontSize={isTablet ? '12' : '15'}>
            Court:
          </Text>
          <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
            {courtLabel}
          </Text>
        </Flex>
        <Flex alignItems="center" gap="4px" mb={isTablet ? '5px' : '12px'}>
          <Text variant="header" fontSize={isTablet ? '12' : '15'}>
            Location:
          </Text>
          <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
            {locationLabel}
          </Text>
        </Flex>
        {address && (
          <Flex alignItems="center" gap="4px">
            <Text variant="header" fontSize={isTablet ? '12' : '15'}>
              Address:
            </Text>
            <ClickableAddress
              address={address}
              variant="subtle"
              fontSize={isTablet ? '11' : '13'}
              fontWeight="400"
              mt="2px"
              textDecoration="underline"
            />
          </Flex>
        )}
      </Box>

      <Divider />
      <Flex p="20px" alignItems="center" gap="10px" justifyContent="space-between">
        <Text fontWeight="600" color="#171C22" fontSize="lg">
          {formatDuration(lesson.duration_minutes)}
        </Text>
        {onCancel && !isCancelled && (
          <Button variant="light" size={isTablet ? 'sm' : undefined} fontWeight="600" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Flex>
    </Box>
  )
}

export default LessonBookingRow
